import { useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { useAuthStore } from '@/lib/auth/store';
import {
  useDialerNames,
  usePilotNumbers,
  useUploadDedicatedCliFile,
} from '../api';
import { useModuleFilter } from '@/lib/permissions/use-module-filter';
import { MODULE_LISTING_KEYS } from '@/lib/constants/modules';

const ALLOWED_TYPES = new Set(['text/plain', 'text/csv']);
const ALLOWED_EXT = /\.(txt|csv)$/i;
const MAX_BYTES = 1 * 1024 * 1024;

export function UploadDedicatedCliFilePage() {
  const navigate = useNavigate();
  const userId = useAuthStore((s) => s.userId);
  const dialers = useDialerNames();
  const filteredDialers = useModuleFilter(dialers.data ?? [], MODULE_LISTING_KEYS.MANAGE_DIALERS, (d) => d.dialerId);
  const pilots = usePilotNumbers();
  const upload = useUploadDedicatedCliFile();

  const fileRef = useRef<HTMLInputElement>(null);
  const [dialerId, setDialerId] = useState<string>('');
  const [pilotNumber, setPilotNumber] = useState<string>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      toast.error('Pick a CLI file to upload.');
      return;
    }
    if (!ALLOWED_TYPES.has(file.type) && !ALLOWED_EXT.test(file.name)) {
      toast.error('Only .txt or .csv files are supported.');
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error('File is larger than 1 MB. Pick a smaller file.');
      return;
    }
    if (!dialerId) {
      toast.error('Pick a dialer.');
      return;
    }
    if (!pilotNumber) {
      toast.error('Pick a pilot number.');
      return;
    }

    upload.mutate(
      {
        cliFile: file,
        userId: userId ?? '',
        pool: 'dedicated',
        dialerId,
        pilotNumber,
      },
      {
        onSuccess: (res) => {
          if (res?.message === 'Parameters Incorrect') {
            toast.error(res.message);
            return;
          }
          toast.success(res?.message ?? 'Dedicated CLI file uploaded');
          navigate('/cli-list');
        },
        onError: () => toast.error('Could not upload the dedicated CLI file.'),
      },
    );
  };

  return (
    <ScrollPage>
      <PageHeader
        title="Upload Dedicated CLI File"
        description="Bulk-import dedicated CLIs from a .txt or .csv file."
        actions={
          <Button asChild variant="outline">
            <Link to="/cli-list">
              <ArrowLeft className="h-4 w-4" />
              Back to CLIs
            </Link>
          </Button>
        }
      />

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <Card>
          <CardHeader>
            <CardTitle>Dedicated CLI file</CardTitle>
            <CardDescription>
              One CLI per line. Max size 1&nbsp;MB. Allowed types: .txt, .csv.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cli-file">File</Label>
              <Input
                id="cli-file"
                ref={fileRef}
                type="file"
                accept=".txt,.csv,text/plain,text/csv"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="dialer">Dialer</Label>
                <Select value={dialerId} onValueChange={setDialerId}>
                  <SelectTrigger id="dialer">
                    <SelectValue
                      placeholder={
                        dialers.isLoading
                          ? 'Loading dialers…'
                          : 'Select a dialer'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredDialers.map((d) => (
                      <SelectItem key={d.dialerId} value={String(d.dialerId)}>
                        {d.dialerName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="pilot">Pilot number</Label>
                <Select value={pilotNumber} onValueChange={setPilotNumber}>
                  <SelectTrigger id="pilot">
                    <SelectValue
                      placeholder={
                        pilots.isLoading
                          ? 'Loading pilots…'
                          : 'Select a pilot number'
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {(pilots.data ?? []).map((p, i) => (
                      <SelectItem
                        key={`${p.pilotNumber}-${i}`}
                        value={String(p.pilotNumber)}
                      >
                        {p.pilotNumber}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex items-center justify-end gap-2">
          <Button asChild variant="ghost">
            <Link to="/cli-list">Cancel</Link>
          </Button>
          <Button type="submit" disabled={upload.isPending}>
            {upload.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <FileUp className="h-4 w-4" />
            )}
            Upload file
          </Button>
        </div>
      </form>
    </ScrollPage>
  );
}
